import { gql, useLazyQuery, useMutation } from '@apollo/client'
import { CheckCircleOutline, HighlightOff } from '@mui/icons-material'
import {
  Box,
  Button,
  CircularProgress,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Alert,
  AlertTitle,
} from '@mui/material'
import {
  Alert as PbxxAlert,
  Brand,
  Mutation,
  Query,
  Retailer,
  User,
} from '@promoboxx/graphql-gateway-types'
import i18n from 'i18n-js'
import { memo, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { makeStyles } from 'tss-react/mui'

import BrandAvatar from '@src/lib/BrandAvatar'
import ChildSpacer from '@src/lib/ChildSpacer'
import { currentRetailerAccountIdSelector } from '@src/stores/app/selectors'

import { ManageBrandTab } from '../brands/ManageBrand'
import useManageBrandDialog from '../brands/useManageBrandDialog'
import { UPDATE_ALERT } from '../shared/TopNav/NotificationsDropdown/NotificationsDropdown'
import useTracking from '../shared/Tracking/useTracking'

const GET_TASK_LIST_ALERTS = gql`
  query getTaskListAlerts($resourceId: ID!) {
    getAlerts(
      resourceType: "retailer_accounts"
      resourceId: $resourceId
      params: { include_retailers: true }
    ) {
      id
      status
      category
      subjectable {
        ... on Retailer {
          id
          brand {
            id
            name
          }
        }
      }
      retailer(params: { fields: [brand] }) {
        id
        brand {
          id
          name
        }
      }
    }
  }
`

interface TaskListItem {
  isMissingCustomFields: boolean
  isMissingLocations: boolean
  isMissingSocialAccounts: boolean
  brand: Brand
  retailerId: Retailer['id']
  alertId: PbxxAlert['id']
}

const MAX = 1
const STORAGE_KEY = `taskList.removeTaskList`
const useStyles = makeStyles()((_theme) => ({
  alertMessage: {
    width: '100%',
  },
}))

const RetailerAccountTaskListAlerts: React.FC<React.PropsWithChildren> = () => {
  const currentRetailerAccountId = useSelector(currentRetailerAccountIdSelector)
  const [getTaskListAlerts, { data, loading }] =
    useLazyQuery<Query>(GET_TASK_LIST_ALERTS)
  const [updateAlert] = useMutation<Mutation>(UPDATE_ALERT)
  const { classes } = useStyles()
  const [shouldShowAll, setShouldShowAll] = useState(false)
  const [selectedRetailer, setSelectedRetailer] = useState<Retailer['id']>()
  const markAlertAsRead = async (alertId: PbxxAlert['id']) => {
    await updateAlert({
      variables: {
        resourceId: alertId,
        params: {
          status: 'read',
        },
      },
    })
  }

  const { track } = useTracking()

  const { showManageBrandDialog } = useManageBrandDialog({
    onClose: async () => {
      // if (refetch) {
      //   await refetch()
      // }

      setSelectedRetailer(undefined)
    },
  })
  const showDialog = (
    retailerId: Retailer['id'],
    initialTab: ManageBrandTab,
  ) => {
    setSelectedRetailer(retailerId)
    showManageBrandDialog({
      retailerId,
      initialTab,
    })
  }

  const currentUser = useSelector(
    (state: { auth: { user: User } }) => state.auth.user,
  )
  const [taskListVisible, setTaskListVisible] = useState(() => {
    const thresholdDate = new Date().getTime() - 1000 * 60 * 60 * 24 // 24 Hours
    const showTaskList = Number(window.localStorage.getItem(STORAGE_KEY) || 0)

    return thresholdDate > showTaskList
  })
  const internalUser = currentUser?.user_roles?.includes('internal')
  const closeTaskList = () => {
    track({
      event: 'Click',
      data: {
        type: 'Clicked on x to close task list',
      },
    })
    window.localStorage.setItem(STORAGE_KEY, String(new Date().getTime()))
    setTaskListVisible(false)
  }

  useEffect(() => {
    getTaskListAlerts({ variables: { resourceId: currentRetailerAccountId } })
    setShouldShowAll(false)
    setSelectedRetailer(undefined)
  }, [currentRetailerAccountId, getTaskListAlerts])

  const taskListItems = useMemo(() => {
    const alerts = data?.getAlerts || []
    const cache: Record<Brand['id'], TaskListItem> = {}

    function getCachedItem(
      brand: Brand,
      retailerId: Retailer['id'],
      alertId: PbxxAlert['id'],
    ) {
      if (!cache[brand.id]) {
        cache[brand.id] = {
          isMissingCustomFields: false,
          isMissingLocations: false,
          isMissingSocialAccounts: false,
          alertId,
          retailerId,
          brand,
        }
      }

      // Safe to bang here because we ensure the record exists above.
      return cache[brand.id]!
    }

    for (const alert of alerts) {
      if (alert.status === 'read') {
        continue
      }

      const brand =
        alert.retailer?.brand ||
        (alert.subjectable?.__typename === 'Retailer' &&
          alert.subjectable.brand)
      const retailerId =
        alert.retailer?.id ||
        (alert.subjectable?.__typename === 'Retailer' && alert.subjectable.id)

      if (!brand || !retailerId) {
        continue
      }

      if (alert.category === 'no_facebook_pages_connected') {
        getCachedItem(brand, retailerId, alert.id!).isMissingSocialAccounts =
          true
      }

      if (alert.category === 'no_twitter_accounts_connected') {
        getCachedItem(brand, retailerId, alert.id!).isMissingSocialAccounts =
          true
      }

      if (alert.category === 'missing_location_on_retailer') {
        getCachedItem(brand, retailerId, alert.id!).isMissingLocations = true
      }

      if (alert.category === 'missing_custom_field_values_for_retailer') {
        getCachedItem(brand, retailerId, alert.id!).isMissingCustomFields = true
      }
    }

    const values = Object.values(cache)
    values.sort((a, b) => {
      return a.brand.name!.localeCompare(b.brand.name!)
    })

    return values
  }, [data])

  let children: React.ReactChild[] = []

  for (const item of taskListItems) {
    let isMissingSocialAccounts = item.isMissingSocialAccounts
    let isMissingCustomFields = item.isMissingCustomFields
    let isMissingLocations = item.isMissingLocations

    if (
      isMissingSocialAccounts ||
      isMissingCustomFields ||
      isMissingLocations
    ) {
      children.push(
        <ListItem
          selected={item.retailerId === selectedRetailer}
          divider
          key={item.retailerId}
        >
          <ListItemAvatar>
            <BrandAvatar brand={item.brand} />
          </ListItemAvatar>
          <ListItemText
            primaryTypographyProps={{
              gutterBottom: true,
            }}
            primary={item.brand?.name || ''}
            secondary={
              <ChildSpacer size={2}>
                {isMissingSocialAccounts ? (
                  <OkOrNotButton
                    isOk={!isMissingSocialAccounts}
                    title={i18n.t('manageBrands.socialAccounts')}
                    onClick={() => {
                      track({
                        event: 'Click',
                        data: {
                          type: 'Clicked on Social Accounts',
                        },
                      })
                      markAlertAsRead(item.alertId)
                      showDialog(item.retailerId, 'social')
                    }}
                  />
                ) : null}
                {isMissingLocations ? (
                  <OkOrNotButton
                    isOk={!isMissingLocations}
                    title={i18n.t('manageBrands.locations')}
                    onClick={() => {
                      track({
                        event: 'Click',
                        data: {
                          type: 'Clicked on Locations',
                        },
                      })
                      markAlertAsRead(item.alertId)
                      showDialog(item.retailerId, 'locations')
                    }}
                  />
                ) : null}
                {isMissingCustomFields ? (
                  <OkOrNotButton
                    isOk={!isMissingCustomFields}
                    title={i18n.t('manageBrands.brandQuestions')}
                    onClick={() => {
                      track({
                        event: 'Click',
                        data: {
                          type: 'Clicked on Brand Questions',
                        },
                      })
                      markAlertAsRead(item.alertId)
                      showDialog(item.retailerId, 'custom_fields')
                    }}
                  />
                ) : null}
              </ChildSpacer>
            }
          />
        </ListItem>,
      )
    }
  }

  if (children.length === 0 || !taskListVisible) {
    return null
  }

  return (
    <Alert
      icon={
        loading ? (
          <CircularProgress
            // We are tied to the size of a material-ui icon here.
            size={22}
          />
        ) : undefined
      }
      severity="warning"
      classes={{ message: classes.alertMessage }}
      onClose={internalUser ? closeTaskList : undefined}
    >
      <AlertTitle>
        {i18n.t('taskList.header', { count: children.length })}
      </AlertTitle>
      <List disablePadding>
        {shouldShowAll ? children : children.slice(0, MAX)}
      </List>

      {children.length > MAX ? (
        <Box textAlign="center" marginTop={2}>
          <Button
            onClick={() => {
              track({
                event: 'Click',
                data: {
                  type: `Clicked on ${
                    shouldShowAll ? 'Show Less' : 'Show More'
                  }`,
                },
              })
              setShouldShowAll((value) => !value)
            }}
            variant="outlined"
          >
            {shouldShowAll
              ? i18n.t('shared.showLess')
              : i18n.t('shared.showMore')}
          </Button>
        </Box>
      ) : null}
    </Alert>
  )
}

interface OkOrNotButtonProps {
  isOk: boolean
  title: string
  onClick?: (event: React.MouseEvent) => void
}

const OkOrNotButton: React.FC<React.PropsWithChildren<OkOrNotButtonProps>> = (
  props,
) => {
  return (
    <Button
      startIcon={
        props.isOk ? <CheckCircleOutline /> : <HighlightOff color="error" />
      }
      variant="outlined"
      size="small"
      onClick={(event) => {
        event.stopPropagation()
        event.preventDefault()
        props.onClick?.(event)
      }}
    >
      <span>{props.title}</span>
    </Button>
  )
}

export default memo(RetailerAccountTaskListAlerts)
